import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, RefreshCw } from 'lucide-react';
import { useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  getGetAttachmentsListQueryKey,
  useDeleteAttachmentsById,
  useGetAttachmentsList,
} from '@/api/generated/attachments/attachments';
import {
  getGetUserAssistantPreferencesQueryKey,
  useGetUserAssistantPreferences,
  usePostUserAssistantPreferences,
  usePostUserNarrativePreferences,
  usePutUserAssistantPreferencesProfile,
  usePutUserLocale,
} from '@/api/generated/user/user';
import leftwardIcon from '@/assets/icons/leftwardIcon.svg';
import plusIcon from '@/assets/icons/plusIcon.svg';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LocaleFlag } from '@/components/ui/locale-flag';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { TabsContent } from '@/components/ui/tabs';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import { signOutDevice } from '@/config/auth/deviceToken';
import { logoutMsal } from '@/config/auth/msal';
import {
  DEFAULT_LOCALE_TAG,
  LANGUAGE_OPTIONS,
  LOCALE_STORAGE_KEY,
  isSupportedTag,
  tagToLocaleEnum,
} from '@/config/i18n/locale';
import { queryKeys } from '@/config/query/queryKeys';
import { NarrativeUploadDialog } from '@/features/narrative/components/NarrativeUploadDialog';
import { useNarrativeUpload } from '@/features/narrative/hooks/useNarrativeUpload';
import { LogoutDialog } from '@/features/profile/components/LogoutDialog';
import { MailboxConnectionCard } from '@/features/profile/components/MailboxConnectionCard';
import { NarrativeExample } from '@/features/profile/components/NarrativeExample';
import { PreferenceSection } from '@/features/profile/components/PreferenceSection';
import { ProfileNarrativeDropzone } from '@/features/profile/components/ProfileNarrativeDropzone';
import {
  type ProfileNarrativeFile,
  ProfileNarrativeUploads,
} from '@/features/profile/components/ProfileNarrativeUploads';
import { ProfileTabs } from '@/features/profile/components/ProfileTabs';
import { useGenerateProfile } from '@/features/profile/hooks/useGenerateProfile';
import { useUserInfo } from '@/utils/hooks/useUserInfo';
import { ROUTES } from '@/utils/routeUtils';

const setStateReducer = <T,>(prev: T, action: React.SetStateAction<T>): T =>
  typeof action === 'function' ? (action as (prevState: T) => T)(prev) : action;

type EditablePreferenceSection = 'drafting' | 'general';

export function ProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { i18n, t } = useTranslation();
  const posthog = usePostHog();
  const updateLocaleMutation = usePutUserLocale();
  const [activeTab, setActiveTab] = useState('narratives');
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { email } = useUserInfo();

  // Optimistic state for personalization toggle during mutation
  const [optimisticEnabled, setOptimisticEnabled] = useState<boolean | null>(null);
  const [isNarrativeExampleLoading, setIsNarrativeExampleLoading] = useReducer(
    setStateReducer<boolean>,
    false,
  );
  const [narrativeExampleContent, setNarrativeExampleContent] = useReducer(
    setStateReducer<string | null>,
    null,
  );

  // Profile editing state
  const [editingSection, setEditingSection] = useState<EditablePreferenceSection | null>(null);
  const [editDrafting, setEditDrafting] = useState('');
  const [editGeneral, setEditGeneral] = useState('');
  const [isPreferenceTransitioning, setIsPreferenceTransitioning] = useState(false);
  const preferenceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (preferenceTimerRef.current !== undefined) {
        clearTimeout(preferenceTimerRef.current);
      }
    };
  }, []);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

  const currentLocale = i18n.resolvedLanguage ?? i18n.language;
  const activeLocale = isSupportedTag(currentLocale) ? currentLocale : DEFAULT_LOCALE_TAG;

  const { data: attachmentsData, isLoading: isAttachmentsLoading } = useGetAttachmentsList(
    { purpose: 'NARRATIVE' },
    { query: { enabled: activeTab === 'narratives' } },
  );
  const uploadedNarratives: Array<ProfileNarrativeFile> = Array.isArray(attachmentsData)
    ? attachmentsData.map((item) => ({
        id: item.id,
        name: item.fileName,
      }))
    : [];

  const deleteAttachmentMutation = useDeleteAttachmentsById();

  const { mutate: runNarrativePreferences } = usePostUserNarrativePreferences({
    mutation: {
      onError: () => {
        setIsNarrativeExampleLoading(false);
      },
      onSuccess: (response) => {
        setNarrativeExampleContent(response.narrativePreferences?.narrativeStyle ?? null);
        setIsNarrativeExampleLoading(false);
      },
      retry: false,
    },
  });

  useEffect(() => {
    if (Array.isArray(attachmentsData)) {
      const files = attachmentsData;
      const ids = files.map((item) => item.id).filter(Boolean);
      if (ids.length > 0) {
        setIsNarrativeExampleLoading(true);
        setNarrativeExampleContent(null);
        runNarrativePreferences({
          data: {
            sampleNarratives: ids,
          },
        });
      } else {
        setNarrativeExampleContent(null);
        setIsNarrativeExampleLoading(false);
      }
    } else {
      setNarrativeExampleContent(null);
    }
  }, [attachmentsData, runNarrativePreferences]);

  // Prevent browser from navigating away when files are dragged over / dropped
  useEffect(() => {
    const handleWindowDragOver = (event: DragEvent) => {
      event.preventDefault();
    };
    const handleWindowDrop = (event: DragEvent) => {
      event.preventDefault();
    };

    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, []);

  // File upload hook
  const {
    fileInputRef,
    handleCancelFile,
    handleClose,
    handleDropFiles,
    handleFileInputChange,
    handleRetry,
    handleRetryFile,
    handleUploadClick,
    setShowUploadDialog,
    showUploadDialog,
    uploadFiles,
  } = useNarrativeUpload({
    onUploadSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getGetAttachmentsListQueryKey({ purpose: 'NARRATIVE' }),
      });
      toast.success(t('profile.toast.narrativeUploadSuccess'));
    },
    purpose: 'NARRATIVE',
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const assistantPreferencesQuery = useGetUserAssistantPreferences({
    query: {
      enabled: activeTab === 'draft-email',
      select: (response) => response,
      staleTime: 10_000,
    },
  });
  const isAssistantPreferencesInitializing =
    activeTab === 'draft-email' &&
    !assistantPreferencesQuery.data &&
    assistantPreferencesQuery.isFetching;

  const enablePersonalization =
    optimisticEnabled ?? assistantPreferencesQuery.data?.preferencesEnabled ?? false;
  const generalPreferences =
    optimisticEnabled === false
      ? ''
      : (assistantPreferencesQuery.data?.assistantPreferences?.generalInfo ?? '');
  const draftingPreferences =
    optimisticEnabled === false
      ? ''
      : (assistantPreferencesQuery.data?.assistantPreferences?.draftingPreferences ?? '');

  const hasExistingPreferences = !!(draftingPreferences || generalPreferences);
  const isEditingDrafting = editingSection === 'drafting';
  const isEditingGeneral = editingSection === 'general';

  const assistantPreferencesMutation = usePostUserAssistantPreferences({
    mutation: {
      onError: (error) => {
        toast.error('Failed to update writing preferences', {
          description: error.message ?? t('profile.toast.writePrefsUnexpected'),
        });
        setOptimisticEnabled(null);
      },
      onSuccess: async (response) => {
        if (response.preferencesEnabled) {
          posthog.capture(ANALYTICS_EVENTS.ASSISTANT_MEMORY_ENABLED, { source: 'profile' });
        } else {
          posthog.capture(ANALYTICS_EVENTS.ASSISTANT_MEMORY_DISABLED, {
            memoryDisabledCount: 1,
            source: 'profile',
          });
        }
        await queryClient.invalidateQueries({ queryKey: getGetUserAssistantPreferencesQueryKey() });
        setOptimisticEnabled(null);
        toast.success(t('profile.toast.writePrefsUpdated'));
      },
      retry: false,
    },
  });

  const handlePersonalizationToggle = (checked: boolean) => {
    setOptimisticEnabled(checked);
    if (!checked) {
      setEditingSection(null);
      setEditDrafting('');
      setEditGeneral('');
      setIsPreferenceTransitioning(false);
    }
    assistantPreferencesMutation.mutate({ data: { enableMemory: checked } });
  };

  const generateProfileMutation = useGenerateProfile({
    onError: (error) => {
      setIsPreferenceTransitioning(false);
      toast.error(t('profile.toast.generateProfileFailed'), {
        description: error.message ?? t('profile.toast.writePrefsUnexpected'),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getGetUserAssistantPreferencesQueryKey() });
      preferenceTimerRef.current = window.setTimeout(
        () => setIsPreferenceTransitioning(false),
        320,
      );
      toast.success(t('profile.toast.profileGenerated'));
    },
  });

  const handleGenerateProfile = () => {
    if (hasExistingPreferences) {
      setShowGenerateConfirm(true);
    } else {
      setIsPreferenceTransitioning(true);
      generateProfileMutation.mutate();
    }
  };

  const isRegeneratingPreferences =
    isPreferenceTransitioning || (generateProfileMutation.isPending && hasExistingPreferences);
  const preferencePlaceholder = enablePersonalization
    ? t('profile.draft.preferencesLearning')
    : t('profile.draft.preferencesPlaceholder');

  const handleConfirmGenerate = () => {
    setShowGenerateConfirm(false);
    setEditingSection(null);
    setIsPreferenceTransitioning(true);
    generateProfileMutation.mutate();
  };

  // Save profile mutation
  const saveProfileMutation = usePutUserAssistantPreferencesProfile({
    mutation: {
      onError: (error) => {
        toast.error(t('profile.toast.profileSaveFailed'), {
          description: error.message ?? t('profile.toast.writePrefsUnexpected'),
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetUserAssistantPreferencesQueryKey() });
        setEditingSection(null);
        toast.success(t('profile.toast.profileSaved'));
      },
    },
  });

  const handleStartEditing = (section: EditablePreferenceSection) => {
    if (!hasExistingPreferences) {
      toast.info(t('profile.draft.preferencesLearning'));
      return;
    }
    if (section === 'drafting') {
      setEditDrafting(draftingPreferences);
    } else {
      setEditGeneral(generalPreferences);
    }
    setEditingSection(section);
  };

  const handleCancelEditing = (section: EditablePreferenceSection) => {
    if (section === 'drafting') {
      setEditDrafting(draftingPreferences);
    } else {
      setEditGeneral(generalPreferences);
    }
    setEditingSection(null);
  };

  const handleSaveProfile = (section: EditablePreferenceSection) => {
    const nextDrafting = section === 'drafting' ? editDrafting : draftingPreferences;
    const nextGeneral = section === 'general' ? editGeneral : generalPreferences;

    saveProfileMutation.mutate({
      data: {
        draftingPreferences: nextDrafting,
        generalInfo: nextGeneral,
      },
    });
  };

  const handleDeleteNarrative = async (fileId: string) => {
    try {
      await deleteAttachmentMutation.mutateAsync({ id: fileId });
      // Refresh list from API so UI stays in sync with backend
      await queryClient.invalidateQueries({
        queryKey: getGetAttachmentsListQueryKey({ purpose: 'NARRATIVE' }),
      });
    } catch (error) {
      console.error('Failed to delete narrative attachment:', error);
      toast.error(t('profile.toast.deleteFileFailed'));
    }
  };

  // Uploaded files are persisted via the attachments bulk-upload API; once they
  // complete, refresh relies on the list query and we just close the dialog here.
  useEffect(() => {
    const completedFiles = uploadFiles.filter((f) => f.status === 'completed');

    if (completedFiles.length > 0) {
      // After uploads complete, rely on list API to refresh state
      // Closing dialog is still handled here to preserve UX.
      setShowUploadDialog(false);
    }
  }, [uploadFiles, setShowUploadDialog]);

  // Handle dialog close
  const handleDialogClose = () => {
    handleClose();
  };

  // Logout ends the login session only — the mailbox connection (Graph
  // credential, background sync, automations) is left intact and is managed
  // separately via the MailboxConnectionCard.
  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Revoke the device session (and underlying Clerk session)
      await signOutDevice();
      // Forget the cached mailbox credential (shared machines)
      await logoutMsal();
      posthog.reset();
      // Clear query cache
      queryClient.removeQueries({ queryKey: queryKeys.auth.all });
      // Navigate to login
      navigate(ROUTES.LOGIN);
    },
    onError: (error) => {
      console.error('Logout failed:', error);
    },
  });

  return (
    <div className="profile-gradient-bg flex h-full flex-1 flex-col overflow-hidden">
      <div className="max-w-page mx-auto flex h-full w-full flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b-half border-border-subtle flex min-h-15.5 w-full shrink-0 items-start justify-between gap-3 border-b px-3 py-2.5">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <div className="flex shrink-0 pt-0.5">
              <Button
                aria-label={t('profile.header.backAria')}
                className="flex h-auto w-auto cursor-pointer items-center p-0 hover:opacity-70"
                onClick={handleBack}
                size="icon"
                variant="ghost"
              >
                <img
                  alt={t('profile.header.backAlt')}
                  className="icon-themed h-4 w-4"
                  src={leftwardIcon}
                />
              </Button>
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex min-w-0 items-center">
                <h1 className="text-foreground m-0 p-0 font-['Inter'] text-[14px] leading-[160%] font-semibold tracking-normal">
                  {t('profile.header.title')}
                </h1>
              </div>
              <p className="text-tertiary m-0 p-0 font-['Inter'] text-[11px] leading-[160%] font-medium tracking-normal wrap-break-word">
                {t('profile.header.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Settings block: Lucio account (+ logout), mailbox connection, and
            language — one bordered section, consistent row styling */}
        <div className="border-b-half flex w-full shrink-0 flex-col gap-1.5 border-b border-[#E0E0E0] px-3 py-2">
          <MailboxConnectionCard onLogoutClick={handleLogoutClick} />

          {/* Language selector */}
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 flex-col">
              <p className="text-tertiary m-0 p-0 font-['Inter'] text-[11px] leading-[160%] font-medium">
                {t('profile.language.title')}
              </p>
              <p className="text-primary m-0 truncate p-0 font-['Inter'] text-[12px] leading-[160%] font-medium">
                {LANGUAGE_OPTIONS.find((option) => option.value === activeLocale)?.label ??
                  activeLocale}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label={t('profile.language.tooltip')}
                  className="h-7 shrink-0 rounded-md border border-[#E0E0E0] bg-white px-2.5 text-xs text-[#333333] hover:bg-gray-50"
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  {t('profile.language.change')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-52 rounded-[14px] border border-[#E6E6E6] bg-white p-1.5 shadow-[0_6px_16px_0_#71778B1A]"
                sideOffset={6}
              >
                {LANGUAGE_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    aria-checked={activeLocale === option.value}
                    className={clsx(
                      "flex h-9 cursor-pointer items-center justify-between rounded-[9px] px-2.5 font-['Inter'] text-[13px] leading-none font-normal text-[#0D0D0D] transition-colors duration-150 focus:bg-[#F5F5F5] data-[highlighted]:bg-[#F5F5F5]",
                      activeLocale === option.value && 'bg-[#F7F7F7] font-medium',
                    )}
                    key={option.value}
                    onSelect={() => {
                      if (!isSupportedTag(option.value)) {
                        return;
                      }
                      const localeEnum = tagToLocaleEnum(option.value);
                      if (!localeEnum) {
                        return;
                      }
                      window.localStorage?.setItem(LOCALE_STORAGE_KEY, option.value);
                      void i18n.changeLanguage(option.value);
                      updateLocaleMutation.mutate(
                        { data: { locale: option.value } },
                        {
                          onSuccess: () => {
                            void queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() });
                          },
                        },
                      );
                    }}
                    role="menuitemradio"
                  >
                    <span className="flex items-center gap-2">
                      <LocaleFlag className="h-4 w-4" locale={option.value} />
                      <span>{option.label}</span>
                    </span>
                    {activeLocale === option.value ? (
                      <Check
                        aria-hidden
                        className="animate-in fade-in-0 zoom-in-50 h-4 w-4 text-[#0D0D0D] duration-200"
                        strokeWidth={2}
                      />
                    ) : (
                      <span aria-hidden className="h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Build version — release tag on prod deploys, dev-<sha> elsewhere */}
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 flex-col">
              <p className="text-tertiary m-0 p-0 font-['Inter'] text-[11px] leading-[160%] font-medium">
                {t('profile.version.title')}
              </p>
              <p className="text-primary m-0 truncate p-0 font-['Inter'] text-[12px] leading-[160%] font-medium">
                {__APP_RELEASE__}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ProfileTabs onValueChange={setActiveTab} value={activeTab}>
          <TabsContent
            className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden"
            value="narratives"
          >
            {isAttachmentsLoading ? (
              <div className="flex flex-1 flex-col gap-1 px-3 pt-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    className="border-border-subtle bg-card flex h-11 w-full items-center gap-2 overflow-hidden rounded-lg border-[0.5px] px-2.5 shadow-[0px_1px_5px_0px_#71778B1A]"
                    key={i}
                  >
                    <Skeleton className="h-5 w-5 shrink-0 rounded-[3px]" />
                    <Skeleton className="h-3 flex-1 rounded" />
                    <Skeleton className="h-3.5 w-3.5 shrink-0 rounded" />
                  </div>
                ))}
              </div>
            ) : uploadedNarratives.length === 0 ? (
              <ProfileNarrativeDropzone onDropFiles={handleDropFiles}>
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                  <div className="mb-2">
                    <h2 className="text-secondary mb-1 text-center font-['Inter'] text-[16px] leading-[160%] font-semibold tracking-normal">
                      {t('profile.narratives.empty.title')}
                    </h2>
                  </div>
                  <p className="text-foreground mb-6 max-w-75 text-center font-['Inter'] text-[12px] leading-[170%] font-normal tracking-normal">
                    {t('profile.narratives.empty.description')}
                  </p>
                  <Button
                    className="hover:bg-primary-active bg-primary-active text-primary-foreground h-7 w-auto min-w-15 gap-1 rounded-md px-4 font-medium whitespace-nowrap"
                    onClick={handleUploadClick}
                  >
                    {t('profile.narratives.empty.uploadButton')}
                  </Button>
                </div>
              </ProfileNarrativeDropzone>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
                <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-3 pt-2">
                  <div className="mb-2">
                    <Button
                      aria-label={t('profile.tabs.addFile')}
                      className="border-border-subtle bg-back-bg2 text-foreground hover:bg-elevated-subtle h-8 rounded-[10px] border px-3 font-['Inter'] text-[12px] font-medium shadow-none transition-all duration-200 ease-out"
                      onClick={handleUploadClick}
                      type="button"
                      variant="outline"
                    >
                      <img
                        alt={t('profile.tabs.addAlt')}
                        className="icon-themed h-3.5 w-3.5"
                        src={plusIcon}
                      />
                      {t('profile.tabs.addFile')}
                    </Button>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <ProfileNarrativeUploads
                      files={uploadedNarratives}
                      onDelete={handleDeleteNarrative}
                    />
                  </div>
                </div>
                <div
                  className="bg-back-bg2 shrink-0 px-3 pt-3 pb-3"
                  style={{ margin: '0 auto', maxWidth: '500px', width: '100%' }}
                >
                  <NarrativeExample
                    content={narrativeExampleContent ?? ''}
                    isLoading={isNarrativeExampleLoading}
                    onRefresh={() => {
                      const ids = uploadedNarratives.map((f) => f.id).filter(Boolean);
                      if (ids.length > 0) {
                        posthog.capture(ANALYTICS_EVENTS.PROFILE_NARRATIVE_PREVIEW_REGENERATED, {
                          locale: activeLocale,
                          narrativePreviewRegenerateCount: 1,
                          sampleNarrativeCount: ids.length,
                        });
                        setIsNarrativeExampleLoading(true);
                        setNarrativeExampleContent(null);
                        runNarrativePreferences({
                          data: {
                            sampleNarratives: ids,
                          },
                        });
                      }
                    }}
                    onSave={(content) => {
                      posthog.capture(ANALYTICS_EVENTS.PROFILE_NARRATIVE_PREVIEW_EDITED);
                      runNarrativePreferences(
                        { data: { narrativeStyle: content } },
                        {
                          onError: () => toast.error(t('profile.toast.profileSaveFailed')),
                          onSuccess: () => toast.success(t('profile.toast.profileSaved')),
                        },
                      );
                    }}
                  />
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent
            className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden px-3 pt-2 pb-4"
            value="draft-email"
          >
            <div className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-1 flex-col gap-0.5">
                  <label className="text-foreground font-['Inter'] text-[12px] leading-[170%] font-medium tracking-normal">
                    {t('profile.draft.enablePersonalization.label')}
                  </label>
                  <p className="text-tertiary font-['Inter'] text-[11px] leading-[160%] font-normal tracking-normal">
                    {t('profile.draft.enablePersonalization.description')}
                  </p>
                </div>
                <Switch
                  checked={enablePersonalization}
                  className="data-[state=checked]:border-primary-active data-[state=checked]:bg-primary-active border-primary-active bg-neutral-mid mt-0.5 border"
                  disabled={
                    assistantPreferencesMutation.isPending || isAssistantPreferencesInitializing
                  }
                  onCheckedChange={handlePersonalizationToggle}
                />
              </div>

              {enablePersonalization && (
                <div className="flex items-center gap-2 transition-all duration-300 ease-out">
                  <Button
                    className="bg-primary-active text-primary-foreground hover:bg-primary-active h-8 flex-1 gap-2 rounded-md font-medium transition-colors duration-300 ease-out disabled:opacity-50"
                    disabled={generateProfileMutation.isPending || editingSection !== null}
                    onClick={handleGenerateProfile}
                  >
                    <RefreshCw
                      className={`h-3.5 w-3.5 ${generateProfileMutation.isPending ? 'animate-spin' : ''}`}
                    />
                    <span key={generateProfileMutation.isPending ? 'regenerating' : 'idle'}>
                      {generateProfileMutation.isPending
                        ? hasExistingPreferences
                          ? t('profile.draft.regeneratingPreferences')
                          : t('profile.draft.generatingProfile')
                        : hasExistingPreferences
                          ? t('profile.draft.regeneratePreferences')
                          : t('profile.draft.generateProfile')}
                    </span>
                  </Button>
                </div>
              )}

              {enablePersonalization && (
                <PreferenceSection
                  autoFocus
                  content={draftingPreferences}
                  editDisabled={editingSection !== null}
                  editValue={editDrafting}
                  isEditing={isEditingDrafting}
                  isRegenerating={isRegeneratingPreferences}
                  isSaving={saveProfileMutation.isPending}
                  label={t('profile.draft.drafting.label')}
                  onCancel={() => handleCancelEditing('drafting')}
                  onEditValueChange={setEditDrafting}
                  onSave={() => handleSaveProfile('drafting')}
                  onStartEdit={() => handleStartEditing('drafting')}
                  placeholder={preferencePlaceholder}
                  saveDisabled={saveProfileMutation.isPending || !editDrafting.trim()}
                />
              )}

              {enablePersonalization && (
                <PreferenceSection
                  content={generalPreferences}
                  editDisabled={editingSection !== null}
                  editValue={editGeneral}
                  isEditing={isEditingGeneral}
                  isRegenerating={isRegeneratingPreferences}
                  isSaving={saveProfileMutation.isPending}
                  label={t('profile.draft.general.label')}
                  onCancel={() => handleCancelEditing('general')}
                  onEditValueChange={setEditGeneral}
                  onSave={() => handleSaveProfile('general')}
                  onStartEdit={() => handleStartEditing('general')}
                  placeholder={preferencePlaceholder}
                  saveDisabled={saveProfileMutation.isPending || !editGeneral.trim()}
                />
              )}
            </div>
          </TabsContent>
        </ProfileTabs>
      </div>

      {/* Hidden File Input */}
      <input
        accept=".pdf,.doc,.docx"
        aria-label={t('profile.narratives.uploadInputAria')}
        className="hidden"
        multiple
        onChange={handleFileInputChange}
        ref={fileInputRef}
        type="file"
      />

      {/* Upload Narratives Dialog */}
      <NarrativeUploadDialog
        files={uploadFiles}
        onCancelFile={handleCancelFile}
        onClose={handleDialogClose}
        onOpenChange={setShowUploadDialog}
        onRetry={handleRetry}
        onRetryFile={handleRetryFile}
        open={showUploadDialog}
      />

      {/* Logout Confirmation Dialog */}
      <LogoutDialog
        email={email}
        isPending={logoutMutation.isPending}
        onConfirm={logoutMutation.mutateAsync}
        onOpenChange={setShowLogoutDialog}
        open={showLogoutDialog}
      />

      {/* Generate Profile Confirmation Dialog */}
      <AlertDialog onOpenChange={setShowGenerateConfirm} open={showGenerateConfirm}>
        <AlertDialogContent className="max-w-[calc(100%-2rem)] gap-2.5 p-3">
          <AlertDialogHeader className="gap-0.5">
            <AlertDialogTitle className="text-secondary text-left font-['Inter'] text-[13px] leading-[170%] font-medium">
              {t('profile.draft.confirmGenerate.title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-tertiary mt-0.5 text-left font-['Inter'] text-[12px] leading-[160%] font-normal">
              {t('profile.draft.confirmGenerate.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-1.5 flex-row justify-end gap-1.5">
            <AlertDialogCancel
              className="border-secondary text-secondary hover:bg-back-bg2 bg-popover m-0 h-7 rounded-md border px-2.5 py-1.5 text-xs"
              onClick={() => setShowGenerateConfirm(false)}
              size="sm"
              variant="outline"
            >
              {t('profile.draft.confirmGenerate.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary text-primary-foreground hover:bg-primary m-0 inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs hover:opacity-90"
              onClick={handleConfirmGenerate}
            >
              {t('profile.draft.confirmGenerate.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
